import React, { useEffect, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import { Button, Form, FormGroup } from 'reactstrap';
import FormItem from '../../../common/forms/FormItem';
import Content from '../../../layout/Content';
import { ACTIVITY_BY_CABIN_CREW_PATH } from '../../../../config/paths';
import { breadcrumbsPropTypes } from '../../../common/resource/proptypes/CommonPropTypes';
import {
  clearCabinCrewActivity,
  getCabinCrewActivity,
} from '../../../../actions/traffic/CabinCrewActivity';
import Table from '../../../common/Table';
import Loader from '../../../common/Loader';
import Modal from '../../../common/modal/Modal';
import { clearHolidayDates, getHolidayDates, postCabinCrewMovementHistory } from '../../../../actions';
import { tzNormalizeDate } from '../../../../utils/date';
import { isRequired, validateEndDate } from '../../../../utils/validators';
import CabinCrewSelect from '../../../common/forms/select/CabinCrewSelect';
import FormFooter from '../../../common/forms/FormFooter';
import DatePicker from '../../../common/forms/input/DatePicker';
import CabinCrewMovementHistoryForm from '../cabin-crew-movement-history/CabinCrewMovementHistoryForm';

const validateDate = (value, allValues) =>
  validateEndDate(value, allValues.fromDateString);

const CabinCrewActivity = ({
  breadcrumbs,
  cabinCrewActivity,
  loading,
  handleSubmit,
  dispatchClearCabinCrewActivity,
  dispatchGetMonthlyCabinCrewActivity,
  dispatchPostCabinCrewMovementHistory,
  holidayDates,
  dispatchGetHolidayDates,
  dispatchClearHolidayDates,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [initialValues, setInitialValues] = useState(null);
  const [lastSearchParams, setLastSearchParams] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);

  useEffect(
    () => () => {
      dispatchClearCabinCrewActivity();
      dispatchClearHolidayDates();
    },
    [],
  );

  const cabinCrew = useMemo(() => cabinCrewActivity.toJS(), [cabinCrewActivity]);

  const rows = useMemo(() => {
    if (!cabinCrew || !cabinCrew.days) return [];

    return Object.entries(cabinCrew.days).map(([date, activity]) => ({
      date,
      activity: activity || '',
    }));
  }, [cabinCrew]);

  const handleCreateActivity = useCallback(
    (dateKey) => {
      if (!cabinCrew) return;

      setInitialValues({
        cabinCrew: { value: cabinCrew.cabinCrewId, label: cabinCrew.fullName },
        startDate: tzNormalizeDate({
          date: dateKey,
          format: 'YYYY-MM-DDT00:00',
        }),
        endDate: tzNormalizeDate({ date: dateKey, format: 'YYYY-MM-DDT23:59' }),
      });
      setModalOpen(true);
    },
    [cabinCrew],
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setInitialValues(null);
  }, []);

  const refreshTable = useCallback(() => {
    if (lastSearchParams) {
      dispatchGetMonthlyCabinCrewActivity(lastSearchParams);
    }
  }, [lastSearchParams, dispatchGetMonthlyCabinCrewActivity]);

  const onSubmit = useCallback(
    (formValues) => {
      const newFormValues = {
        fromDateString: tzNormalizeDate({
          date: formValues.fromDateString,
          format: 'YYYY-MM-DD',
        }),
        toDateString: tzNormalizeDate({
          date: formValues.toDateString,
          format: 'YYYY-MM-DD',
        }),
        cabinCrewId: formValues.cabinCrewId.value,
      };

      setLastSearchParams(newFormValues);
      dispatchGetMonthlyCabinCrewActivity(newFormValues);
      dispatchGetHolidayDates(newFormValues);
    },
    [dispatchGetMonthlyCabinCrewActivity],
  );

  const onSubmitMovementHistoryForm = async (formValues) => {
    const newFormValues = {
      ...formValues,
      startDate: tzNormalizeDate({ date: formValues.startDate }),
      endDate: tzNormalizeDate({ date: formValues.endDate }),
      redirect: false,
      chargeMonth: formValues.month ? formValues.month.value : null,
      chargeYear: formValues.year || null,
    };

    setActivityLoading(true);
    await dispatchPostCabinCrewMovementHistory(newFormValues);
    setActivityLoading(false);

    closeModal();
    refreshTable();
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Fecha',
        accessor: 'date',
        className: 'text-center',
        width: 140,
      },
      {
        Header: 'Actividades',
        accessor: 'activity',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value, original }) => {
                if (!value || value === '') {
                  return (
                    <Button
                      color="link"
                      size="sm"
                      // eslint-disable-next-line react/prop-types
                      onClick={() => handleCreateActivity(original.date)}
                      title="Crear actividad"
                    >
                      <i className="fa fa-plus-circle text-muted" />
                    </Button>
                  );
                }
                // eslint-disable-next-line
                const lines = value.split('\n');
                return (
                  <div style={{ whiteSpace: 'pre-line', fontSize: '0.85rem' }}>
                    {lines.map((line, idx) => (
                      // eslint-disable-next-line
                      <div key={idx}>{line}</div>
                    ))}
                  </div>
                );
              },
        className: 'text-center'
      },
    ],
    [handleCreateActivity],
  );

  const holidaySet = useMemo(
      () => new Set(holidayDates ? holidayDates.toJS() : []),
      [holidayDates],
    );

  const content = (
    <>
      <Modal
        id="cabinCrew-movement-history-modal"
        title="Crear Actividad de SAB"
        body={
          <CabinCrewMovementHistoryForm
            onSubmit={onSubmitMovementHistoryForm}
            initialValues={initialValues}
            isEdit={false}
            disableSaveButton={activityLoading}
          />
        }
        show={modalOpen}
        onClickCancel={closeModal}
        onClickClose={closeModal}
      />

      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup row>
          <FormItem label="Tripulante" required>
            <Field
              name="cabinCrewId"
              component={CabinCrewSelect}
              placeholder="Tripulante"
              forHomeBase
              validate={[isRequired]}
            />
          </FormItem>
        </FormGroup>
        <FormGroup row>
          <FormItem label="Desde" required>
            <Field
              name="fromDateString"
              component={DatePicker}
              validate={[isRequired]}
            />
          </FormItem>
        </FormGroup>
        <FormGroup row>
          <FormItem label="Hasta" required>
            <Field
              name="toDateString"
              component={DatePicker}
              validate={[isRequired, validateDate]}
            />
          </FormItem>
        </FormGroup>
        <FormFooter
          saveButtonColor="primary"
          saveButtonText="Buscar"
          saveButtonIcon="fa fa-search"
          saveButtonDisabled={loading}
        />
      </Form>

      {cabinCrew && (
        <div className="border rounded p-3 my-3">
          <h5 className="mb-1">{cabinCrew.fullName}</h5>
          <div className="text-muted">
            DNI {cabinCrew.documentNumber} · {cabinCrew.cabinCrewType} ·{' '}
            {cabinCrew.baseLocation} · {cabinCrew.status}
            {cabinCrew.statusDescription &&
            cabinCrew.statusDescription !== cabinCrew.status
              ? ` (${cabinCrew.statusDescription})`
              : ''}
          </div>
        </div>
      )}

      {cabinCrew && (
        <Table
          columns={columns}
          data={rows}
          loading={loading}
          manual={false}
          showPagination={false}
          defaultPageSize={rows.length > 0 ? rows.length : 31}
          generateStyle={(row) =>
            row && holidaySet.has(row.original.date)
              ? { background: '#7eedf5' }
              : {}
          }
        />
      )}

      {!cabinCrew && !loading && (
        <div className="alert alert-info mt-3">
          <i className="fa fa-info-circle" /> Seleccione un conductor y un rango
          de fechas para ver sus actividades
        </div>
      )}

      {loading && !cabinCrew && <Loader />}
    </>
  );

  return (
    <Content
      title="Tareo por Servicio a Bordo"
      breadcrumbs={breadcrumbs}
      content={content}
    />
  );
};

CabinCrewActivity.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  cabinCrewActivity: PropTypes.oneOfType([
    PropTypes.shape({
      cabinCrewId: PropTypes.number,
      fullName: PropTypes.string,
      days: PropTypes.objectOf(PropTypes.string),
      dayEventTypes: PropTypes.objectOf(PropTypes.string),
    }),
    PropTypes.arrayOf(PropTypes.object),
  ]),
  loading: PropTypes.bool,
  handleSubmit: PropTypes.func.isRequired,
  dispatchClearCabinCrewActivity: PropTypes.func.isRequired,
  dispatchGetMonthlyCabinCrewActivity: PropTypes.func.isRequired,
  dispatchPostCabinCrewMovementHistory: PropTypes.func.isRequired,
  holidayDates: PropTypes.instanceOf(),
  dispatchGetHolidayDates: PropTypes.func.isRequired,
  dispatchClearHolidayDates: PropTypes.func.isRequired,
};

CabinCrewActivity.defaultProps = {
  cabinCrewActivity: null,
  loading: false,
  holidayDates: null,
};

const mapStateToProps = ({ TrafficUnit, HumanResourcesUnit }) => ({
  breadcrumbs: [
    ...TrafficUnit.CabinCrewUnitHome.get('breadcrumbs'),
    { text: 'Tareo por Servicio a Bordo', href: ACTIVITY_BY_CABIN_CREW_PATH },
  ],
  cabinCrewActivity: TrafficUnit.CabinCrewActivity.getIn(['all', 'content']),
  loading: TrafficUnit.CabinCrewActivity.getIn(['all', 'loading']),
  holidayDates: HumanResourcesUnit.Holiday.getIn(['dates', 'content']),
});

const mapDispatchToProps = {
  dispatchClearCabinCrewActivity: clearCabinCrewActivity,
  dispatchGetMonthlyCabinCrewActivity: getCabinCrewActivity,
  dispatchPostCabinCrewMovementHistory: postCabinCrewMovementHistory,
  dispatchGetHolidayDates: getHolidayDates,
  dispatchClearHolidayDates: clearHolidayDates,
};

const formComponent = reduxForm({ form: 'CabinCrewActivityForm' })(CabinCrewActivity);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
